import { Fragment, useId, useRef, type KeyboardEvent } from 'react'
import {
  Activity,
  CalendarCheck,
  DollarSign,
  Hash,
  Percent,
  TextAlignStart,
  type LucideIcon,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import {
  NEW_OBJECTIVE_DIRECTIONS,
  NON_NUMERIC_GOAL_TYPES,
  NUMERIC_GOAL_TYPES,
  type GoalType,
} from '@/features/ogsm/new-objective'
import { OGSM_COPY } from '@/features/ogsm/ogsm-labels'
import { cn } from '@/lib/utils'
import type { OgsmDirection } from '@/types/api'

const GOAL_TYPE_ICONS: Record<GoalType, LucideIcon> = {
  METRIC: Activity,
  NUMBER: Hash,
  CURRENCY: DollarSign,
  PERCENT: Percent,
  DATE: CalendarCheck,
  TEXT: TextAlignStart,
}

const NEXT_KEYS = new Set(['ArrowRight', 'ArrowDown'])
const PREVIOUS_KEYS = new Set(['ArrowLeft', 'ArrowUp'])

/** Roving tabindex for a radiogroup: one tab stop, arrows move focus and selection, wrapping. */
function useRovingRadios<T extends string>(
  order: readonly T[],
  value: T | null,
  onChange: ((next: T) => void) | undefined,
  disabled: boolean
) {
  const buttons = useRef(new Map<T, HTMLButtonElement>())
  const tabStop = value !== null && order.includes(value) ? value : order[0]

  return {
    ref: (item: T) => (element: HTMLButtonElement | null) => {
      if (element) buttons.current.set(item, element)
      else buttons.current.delete(item)
    },
    tabIndex: (item: T) => (item === tabStop ? 0 : -1),
    onKeyDown: (item: T) => (event: KeyboardEvent<HTMLButtonElement>) => {
      const step = NEXT_KEYS.has(event.key)
        ? 1
        : PREVIOUS_KEYS.has(event.key)
          ? -1
          : 0
      if (disabled || step === 0) return
      event.preventDefault()
      const index = order.indexOf(item)
      const next = order[(index + step + order.length) % order.length]
      if (next === undefined) return
      buttons.current.get(next)?.focus()
      onChange?.(next)
    },
  }
}

export interface GoalTypePickerProps {
  /** Which types show; rows are NUMERIC_GOAL_TYPES then NON_NUMERIC_GOAL_TYPES filtered by this (a row with none is hidden). */
  types: readonly GoalType[]
  value: GoalType
  /** Line under the chips: the type description (create) or the locked hint (edit). */
  description: string
  onChange?: (type: GoalType) => void
  /** Edit: every chip disabled, `value` stays checked. */
  disabled?: boolean
  /** false: no visible Numeric/Non-numeric column (the row label stays for screen readers). */
  rowLabels?: boolean
  /** Accessible name of the radiogroup. */
  label?: string
}

export function GoalTypePicker({
  types,
  value,
  description,
  onChange,
  disabled = false,
  rowLabels = true,
  label = OGSM_COPY.goalType,
}: GoalTypePickerProps) {
  const id = useId()
  const descriptionId = `${id}-description`
  const rows = [
    {
      key: 'numeric',
      label: OGSM_COPY.goalTypeGroups.numeric,
      types: NUMERIC_GOAL_TYPES.filter((type) => types.includes(type)),
    },
    {
      key: 'non-numeric',
      label: OGSM_COPY.goalTypeGroups.nonNumeric,
      types: NON_NUMERIC_GOAL_TYPES.filter((type) => types.includes(type)),
    },
  ].filter((row) => row.types.length > 0)
  const roving = useRovingRadios(
    rows.flatMap((row) => row.types),
    value,
    onChange,
    disabled
  )

  return (
    <div
      role="radiogroup"
      aria-label={label}
      aria-describedby={descriptionId}
      className={cn(
        'grid grid-cols-1 gap-x-4 gap-y-2',
        rowLabels && 'sm:grid-cols-[auto_minmax(0,1fr)] sm:items-center'
      )}
    >
      {rows.map((row) => {
        const labelId = `${id}-${row.key}`
        return (
          <Fragment key={row.key}>
            <Typography
              id={labelId}
              variant="body-sm"
              as="span"
              className={rowLabels ? 'text-primary/50' : 'sr-only'}
            >
              {row.label}
            </Typography>
            <div
              role="group"
              aria-labelledby={labelId}
              className="flex flex-wrap gap-1.5"
            >
              {row.types.map((type) => {
                const Icon = GOAL_TYPE_ICONS[type]
                const checked = type === value
                return (
                  <Button
                    key={type}
                    ref={roving.ref(type)}
                    type="button"
                    role="radio"
                    aria-checked={checked}
                    size="sm"
                    variant={checked ? 'secondary-tint' : 'outline'}
                    // Both variants size svgs differently; pin one size so choosing doesn't jump.
                    // Tighter than `sm` so the four Numeric chips fit one row at 540px, as designed.
                    className="gap-1.5 rounded-full px-2 [&_svg]:size-3.5"
                    disabled={disabled}
                    tabIndex={roving.tabIndex(type)}
                    onClick={() => onChange?.(type)}
                    onKeyDown={roving.onKeyDown(type)}
                  >
                    <Icon aria-hidden="true" />
                    {OGSM_COPY.goalTypes[type]}
                  </Button>
                )
              })}
            </div>
          </Fragment>
        )
      })}
      <Typography
        id={descriptionId}
        variant="body-sm"
        className={cn('text-primary/60', rowLabels && 'sm:col-start-2')}
      >
        {description}
      </Typography>
    </div>
  )
}

export interface DirectionControlProps {
  /** null in edit when the node has no direction: nothing checked. */
  value: OgsmDirection | null
  onChange: (direction: OgsmDirection) => void
}

export function DirectionControl({ value, onChange }: DirectionControlProps) {
  const roving = useRovingRadios(
    NEW_OBJECTIVE_DIRECTIONS,
    value,
    onChange,
    false
  )

  return (
    <div
      role="radiogroup"
      aria-label={OGSM_COPY.direction}
      className="inline-flex w-full max-w-84 rounded-md bg-muted p-1"
    >
      {NEW_OBJECTIVE_DIRECTIONS.map((direction) => {
        const checked = direction === value
        return (
          <button
            key={direction}
            ref={roving.ref(direction)}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={roving.tabIndex(direction)}
            className={cn(
              'h-8 flex-1 cursor-pointer rounded-md border border-transparent text-sm text-primary/60 transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
              checked &&
                'border-secondary bg-background text-secondary hover:text-secondary'
            )}
            onClick={() => onChange(direction)}
            onKeyDown={roving.onKeyDown(direction)}
          >
            {OGSM_COPY.directions[direction]}
          </button>
        )
      })}
    </div>
  )
}
